import React, { Fragment, useContext, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Badge } from 'reactstrap';
import { PlusCircle, Edit, Trash2, Image as ImageIcon, Clock } from 'react-feather';
import { Breadcrumbs } from '../../../../AbstractElements';
import CustomizerContext from '../../../../_helper/Customizer';
import { fetchLoadingScreen, fetchUploadImageScreen, fetchEditImageScreen, fetchDeleteImageScreen } from '../../../../api/loadingScreen';
import { toast } from 'react-toastify';

const LoadingScreenManagement = () => {
  const { layoutURL } = useContext(CustomizerContext);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [displayTime, setDisplayTime] = useState(5);

  // Load images khi component mount
  useEffect(() => {
    loadImages();
  }, []);

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Hàm load danh sách loading screen
  const loadImages = async () => {
    setLoading(true);
    try {
      const data = await fetchLoadingScreen();
      setImages(data);
      toast.success('✅ Tải danh sách loading screen thành công!');
    } catch (error) {
      toast.error('❌ Lỗi khi tải loading screen: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý chọn file
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Vui lòng chọn file ảnh!');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File ảnh quá lớn! Vui lòng chọn file nhỏ hơn 5MB.');
        return;
      }

      setSelectedFile(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    }
  };

  // Mở modal thêm mới
  const handleAddNew = () => {
    setEditMode(false);
    setCurrentImage(null);
    setSelectedFile(null);
    setPreviewUrl('');
    setDisplayTime(5);
    setModalOpen(true);
  };

  // Mở modal chỉnh sửa
  const handleEdit = (image) => {
    setEditMode(true);
    setCurrentImage(image);
    setPreviewUrl(image.url);
    setDisplayTime(image.time || 5);
    setSelectedFile(null);
    setModalOpen(true);
  };

  // Xử lý upload/edit image
  const handleSubmit = async () => {
    if (!selectedFile && !editMode) {
      toast.warning('⚠️ Vui lòng chọn file ảnh!');
      return;
    }

    if (displayTime <= 0 || displayTime > 60) {
      toast.warning('⚠️ Thời gian hiển thị phải từ 1-60 giây!');
      return;
    }

    setLoading(true);
    try {
      if (editMode && currentImage) {
        // Edit image
        const formData = new FormData();
        if (selectedFile) {
          formData.append('url', selectedFile);
        }
        formData.append('time', displayTime);
        
        await fetchEditImageScreen(currentImage._id, formData);
        toast.success('✅ Cập nhật loading screen thành công!');
      } else {
        // Upload new image
        await fetchUploadImageScreen(selectedFile, displayTime);
        toast.success('✅ Thêm loading screen mới thành công!');
      }
      
      setModalOpen(false);
      loadImages();
    } catch (error) {
      toast.error('❌ Lỗi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý xóa image
  const handleDelete = async (id) => {
    if (window.confirm('⚠️ Bạn có chắc chắn muốn xóa loading screen này?')) {
      setLoading(true);
      try {
        await fetchDeleteImageScreen(id);
        toast.success('✅ Xóa loading screen thành công!');
        loadImages();
      } catch (error) {
        toast.error('❌ Lỗi khi xóa: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Loading Screen" title="Loading Screen Management" mainTitle="Quản lý Loading Screen" />
      <Container fluid={true}>
        <Row>
          <Col md="12">
            <Card className="shadow-sm border-0">
              <CardBody className="p-4">
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                  <div>
                    <h4 className="mb-1">
                      <ImageIcon size={24} className="me-2 text-primary" />
                      Danh sách Loading Screen
                    </h4>
                    <p className="text-muted mb-0 small">
                      Quản lý hình ảnh hiển thị khi ứng dụng đang tải
                    </p>
                  </div>
                  <Button color="primary" onClick={handleAddNew} className="shadow-sm">
                    <PlusCircle size={16} className="me-2" />
                    Thêm Loading Screen
                  </Button>
                </div>

                {/* Loading State */}
                {loading && (
                  <div className="text-center my-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Đang tải dữ liệu...</p>
                  </div>
                )}

                {/* Empty State */}
                {!loading && images.length === 0 && (
                  <div className="text-center my-5 py-5">
                    <ImageIcon size={80} className="text-muted mb-3" />
                    <h5 className="text-muted">Chưa có loading screen nào</h5>
                    <p className="text-muted small">Hãy thêm loading screen đầu tiên của bạn!</p>
                    <Button color="primary" onClick={handleAddNew} className="mt-3">
                      <PlusCircle size={16} className="me-2" />
                      Thêm ngay
                    </Button>
                  </div>
                )}

                {/* Images Grid */}
                {!loading && images.length > 0 && (
                  <Row>
                    {images.map((image, index) => (
                      <Col xl="3" lg="4" md="6" sm="12" key={image._id || index} className="mb-4">
                        <Card className="shadow-sm border h-100">
                          {/* Image Container */}
                          <div className="position-relative" style={{ paddingTop: '75%', backgroundColor: '#f8f9fa' }}>
                            <img
                              src={image.url}
                              alt={`Loading Screen ${index + 1}`}
                              className="position-absolute top-0 start-0 w-100 h-100"
                              style={{ objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                              }}
                            />
                            {/* Time Badge */}
                            <div className="position-absolute top-0 end-0 m-2">
                              <Badge color="dark" className="d-flex align-items-center">
                                <Clock size={12} className="me-1" />
                                {image.time || 5}s
                              </Badge>
                            </div>
                          </div>

                          {/* Card Body */}
                          <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="flex-grow-1">
                                <small className="text-muted d-block">
                                  ID: {image._id?.slice(-8)}
                                </small>
                                <small className="text-muted d-block">
                                  Hiển thị: <strong>{image.time || 5} giây</strong>
                                </small>
                              </div>
                              <div className="btn-group">
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleEdit(image)}
                                  title="Chỉnh sửa"
                                  className="me-1"
                                >
                                  <Edit size={14} />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDelete(image._id)}
                                  title="Xóa"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}

                {/* Statistics */}
                {!loading && images.length > 0 && (
                  <div className="mt-4 pt-3 border-top">
                    <div className="d-flex justify-content-between align-items-center">
                      <p className="mb-0 text-muted">
                        <strong>Tổng số:</strong> {images.length} loading screen
                      </p>
                      <p className="mb-0 text-muted small">
                        Thời gian hiển thị trung bình: {(images.reduce((acc, img) => acc + (img.time || 5), 0) / images.length).toFixed(1)}s
                      </p>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal Upload/Edit Loading Screen */}
        <Modal isOpen={modalOpen} toggle={handleCloseModal} size="lg">
          <ModalHeader toggle={handleCloseModal}>
            {editMode ? '✏️ Chỉnh sửa Loading Screen' : '➕ Thêm Loading Screen Mới'}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="imageFile" className="fw-bold">
                <ImageIcon size={16} className="me-2" />
                Chọn ảnh Loading Screen <span className="text-danger">*</span>
              </Label>
              <Input
                id="imageFile"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="form-control-lg"
              />
              <small className="text-muted d-block mt-2">
                💡 Chấp nhận: JPG, PNG, GIF. Tối đa: 5MB
              </small>
            </FormGroup>

            <FormGroup>
              <Label for="displayTime" className="fw-bold">
                <Clock size={16} className="me-2" />
                Thời gian hiển thị (giây) <span className="text-danger">*</span>
              </Label>
              <Input
                id="displayTime"
                type="number"
                min="1"
                max="60"
                value={displayTime}
                onChange={(e) => setDisplayTime(Number(e.target.value))}
                className="form-control-lg"
                placeholder="Nhập số giây (1-60)"
              />
              <small className="text-muted d-block mt-2">
                💡 Thời gian ảnh này sẽ hiển thị trên màn hình loading (từ 1-60 giây)
              </small>
            </FormGroup>

            {/* Preview */}
            {previewUrl && (
              <div className="mt-4">
                <Label className="fw-bold">🖼️ Xem trước:</Label>
                <div className="border rounded p-2 bg-light">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-100 rounded"
                    style={{ maxHeight: '400px', objectFit: 'contain' }}
                  />
                  <div className="text-center mt-2">
                    <Badge color="dark">
                      <Clock size={12} className="me-1" />
                      Hiển thị {displayTime} giây
                    </Badge>
                  </div>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleCloseModal} disabled={loading}>
              Hủy
            </Button>
            <Button 
              color="primary" 
              onClick={handleSubmit} 
              disabled={loading || (!selectedFile && !editMode)}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Đang xử lý...
                </>
              ) : (
                editMode ? '💾 Cập nhật' : '➕ Thêm mới'
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </Fragment>
  );
};

export default LoadingScreenManagement;

